import { z } from "zod";

/**
 * Interactions (Phase 10 — Contact 360). A manually-logged touch on a
 * contact (call/text/email/note/meeting) that feeds the contact's timeline
 * alongside the auto-derived lifecycle rows (inquiry/quote/event/etc).
 *
 * GET /api/v2/contacts/{id}/interactions -> {items,next_cursor}
 * POST /api/v2/contacts/{id}/interactions {type,direction?,subject?,body?,occurred_at?}
 * PATCH/DELETE /api/v2/contacts/{id}/interactions/{iid}
 */

export const InteractionTypeSchema = z.enum([
  "call",
  "text",
  "email",
  "note",
  "meeting",
]);
export type InteractionType = z.infer<typeof InteractionTypeSchema>;

export const INTERACTION_TYPES: InteractionType[] = [
  "call",
  "text",
  "email",
  "note",
  "meeting",
];

export const INTERACTION_TYPE_LABELS: Record<InteractionType, string> = {
  call: "Call",
  text: "Text",
  email: "Email",
  note: "Note",
  meeting: "Meeting",
};

// Icons for the timeline — kept alongside the lifecycle-kind icon map in
// contactOverview.ts since both feed the same merged view.
export const INTERACTION_TYPE_ICONS: Record<InteractionType, string> = {
  call: "📞",
  text: "💬",
  email: "✉️",
  note: "📝",
  meeting: "🤝",
};

export const InteractionDirectionSchema = z.enum(["inbound", "outbound", "na"]);
export type InteractionDirection = z.infer<typeof InteractionDirectionSchema>;

export const INTERACTION_DIRECTION_LABELS: Record<InteractionDirection, string> = {
  inbound: "Inbound",
  outbound: "Outbound",
  na: "N/A",
};

export const InteractionSchema = z.object({
  id: z.string(),
  contact_id: z.string(),
  type: InteractionTypeSchema,
  direction: InteractionDirectionSchema,
  subject: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
  occurred_at: z.string(),
  created_at: z.string(),
});
export type Interaction = z.infer<typeof InteractionSchema>;

export const InteractionListResponseSchema = z.object({
  items: z.array(InteractionSchema),
  next_cursor: z.string().nullable(),
});
export type InteractionListResponse = z.infer<typeof InteractionListResponseSchema>;

// ---- Mutation inputs ------------------------------------------------------

export interface CreateInteractionInput {
  contactId: string;
  type: InteractionType;
  direction?: InteractionDirection;
  subject?: string | null;
  body?: string | null;
  occurred_at?: string;
}

export interface PatchInteractionInput {
  contactId: string;
  interactionId: string;
  type?: InteractionType;
  direction?: InteractionDirection;
  subject?: string | null;
  body?: string | null;
  occurred_at?: string;
}

export interface DeleteInteractionInput {
  contactId: string;
  interactionId: string;
}
